"use client";

import { Card, CardContent, Badge, CardHeader, Button } from "visuais";
import {
  Mail,
  Building2,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  ServerCog,
} from "lucide-react";
import { useState } from "react";
import { AvatarCropDialog, useUserAvatarUrl } from "@US";
import { useUser } from "store";
import { toast } from "sonner";

export function UserInfoCard() {
  const {
    id,
    login,
    email,
    departamento,
    cargo,
    status,
    ultimoLogin,
    userDisplay,
  } = useUser();
  const { refetchFileKey } = useUserAvatarUrl(login);
  const [isClearingServerCache, setIsClearingServerCache] = useState(false);

  const handleImageConfirmed = () => {
    refetchFileKey();
  };

  const getStatusBadge = (status: string) => {
    return status === "ativo" ? (
      <Badge variant="default" className="bg-success text-success-foreground">
        <CheckCircle className="w-3 h-3 mr-1" />
        Ativo
      </Badge>
    ) : (
      <Badge variant="destructive">
        <XCircle className="w-3 h-3 mr-1" />
        Bloqueado
      </Badge>
    );
  };

  const canClearServerCache = login === "pedro.leite";

  const handleClearServerCache = async () => {
    setIsClearingServerCache(true);
    const loadingToastId = toast.loading("Limpando cache do servidor...");

    try {
      const response = await fetch("/api/admin/cache/clear", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login: login || "",
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || "Falha ao limpar cache do servidor.");
      }

      toast.success("Cache do servidor limpo com sucesso.", {
        id: loadingToastId,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao limpar cache do servidor.";
      toast.error(message, { id: loadingToastId });
    } finally {
      setIsClearingServerCache(false);
    }
  };

  return (
    <Card className="w-full flex flex-col h-full p-0 shadow-lg shadow-primary/15">
      <CardHeader className="flex flex-col p-6 gap-6 w-full bg-border/40 border-b shadow">
        <div className="flex justify-between gap-5">
          <AvatarCropDialog
            onConfirm={handleImageConfirmed}
            username={login || ""}
          />
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-card-foreground">
              {userDisplay}
            </h2>
            <p className="text-sm text-muted-foreground">@{login}</p>
            <p className="text-sm text-muted-foreground/80">ID: {id}</p>
            <div className="mt-2">{getStatusBadge(status)}</div>
            {canClearServerCache && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void handleClearServerCache()}
                disabled={isClearingServerCache}
              >
                <ServerCog className="w-4 h-4 mr-2" />
                {isClearingServerCache
                  ? "Limpando cache servidor..."
                  : "Limpar cache do servidor"}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 w-full">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2 p-4 rounded-lg bg-secondary/60">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="w-4 h-4" />
              <span className="font-medium">Email</span>
            </div>
            <span className="text-sm font-semibold truncate text-foreground">
              {email || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-2 p-4 rounded-lg bg-secondary/60">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">Departamento</span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              {departamento || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-2 p-4 rounded-lg bg-secondary/60">
            <div className="flex items-center gap-2 text-muted-foreground">
              <User className="w-4 h-4" />
              <span className="font-medium">Cargo</span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              {cargo || "N/A"}
            </span>
          </div>
          <div className="flex flex-col gap-2 p-4 rounded-lg bg-secondary/60">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">Último Login</span>
            </div>
            <span className="text-sm font-semibold text-foreground">
              {ultimoLogin || "N/A"}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
